/**
 * queueOrdering — a queued write that may only replay AFTER another queued write
 * landed (a predecessor), matched on a payload key.
 *
 * WHY (RULING-EC-VISIT-REPORT-V8-OPEN-RULINGS-2026-10-04 R1): a v8 visit submit is
 * TWO calls in a fixed order under one source_event_id — the v2 door mints the visit
 * engagement, the v8 door only SUPPLEMENTS it and is refused ("no visit engagement for
 * source_event_id … submit the visit report first") when it arrives first. Offline,
 * both bodies sit in the same outbox. The flush is FIFO and CONTINUES past a failed
 * entry, so a v2 entry that fails transiently in a cycle would let its v8 entry run in
 * the same cycle and be refused — a deterministic 4xx that pins the v8 entry `failed`
 * for good. FIFO order is not a dependency; this registry makes the dependency explicit.
 *
 * THREE VERDICTS for a dependent entry (predecessorVerdict):
 *   free    — no unlanded predecessor: replay it.
 *   waiting — the predecessor is still in flight (pending / retries left): leave the
 *             dependent PENDING and untouched for this cycle — no attempt, no retry
 *             consumed, no failure recorded.
 *   failed  — the predecessor reached one of the queue's TERMINAL states (pinned failed
 *             at maxRetries, or conflict). The dependent can never replay while that
 *             holds, and "pending forever" is a silent strand: a pending entry is out of
 *             reach of every exit that acts on failed entries. So it takes the queue's
 *             OWN terminal-failure shape (replayFailure.applyPredecessorFailure): pinned
 *             failed, kept in the queue, captured off-device under the registration's
 *             reason code. It is NEVER sent without its predecessor and never dropped.
 *
 * A dependent pinned that way moves again ONLY together with its predecessor
 * (rePendDependentsBehindPendingPredecessors): when a retry / the on-load recovery
 * re-pends the predecessor, the dependent is re-pended behind it. A predecessor that is
 * deterministic or gone leaves the dependent pinned; Discard is its exit.
 *
 * A REMOVED predecessor is the fourth fate (dependentsOrphanedByRemoval). When the rep
 * DISCARDS a predecessor that never landed, its still-pending dependent would read `free`
 * on the next cycle (nothing blocks it any more), be sent alone and be refused. The
 * discard exit pins it instead — the same terminal shape, captured under the
 * registration's `discardedReasonCode` — at the moment the predecessor leaves the queue.
 * The discard is also REMEMBERED by key (recordDiscardedPredecessor): a dependent that is not
 * in the queue yet has nothing to pin, and once the predecessor is gone the queue alone cannot
 * tell "discarded" from "landed" — both are simply absent. The record is dropped again when the
 * dependent's owner will never send it (forgetDiscardedPredecessor), which bounds the store.
 *
 * A caller about to SEND or QUEUE a dependent asks predecessorFate first: waiting / failed /
 * discarded / free (no unlanded predecessor and none discarded — it landed).
 *
 * Store layer: imports only the queue's data contracts + queueStore / queueStoreWrite (LAYER 1), so
 * queueFlush, queueExit, queueRecovery and the handler modules can all depend on it
 * without a cycle (madge).
 */

import type { QueueEntry, QueueFailureReason } from './offlineQueueTypes'
import { isGenuinelyFailed, readDiscardedPredecessors, readQueue } from './queueStore'
import { updateDiscardedPredecessors } from './queueStoreWrite'

export interface QueuePredecessor {
  /** The queue type that must land first. */
  predecessorType: string
  /** The shared key on both payloads (e.g. source_event_id); null = not relatable. */
  keyOf: (payload: unknown) => string | null
  /** The failed-write capture reason a dependent is recorded under when its predecessor
   *  is terminally failed. Declared by the REGISTRATION (the domain names its own code);
   *  this module carries no reason literal of its own. */
  failedReasonCode: string
  /** The failed-write capture reason a still-unsent dependent is recorded under when its
   *  unlanded predecessor is DISCARDED by the rep. Declared by the registration, like
   *  `failedReasonCode`. */
  discardedReasonCode: string
}

/** The failure reason a dependent carries while pinned behind a dead predecessor. */
export const PREDECESSOR_FAILED_REASON = 'predecessor-failed' satisfies QueueFailureReason

const predecessors = new Map<string, QueuePredecessor>()

/** Declare that entries of `type` replay only after their `predecessorType` twin landed. */
export function registerQueuePredecessor(type: string, spec: QueuePredecessor): void {
  predecessors.set(type, spec)
}

/** The synced sentinel (status failed with retries left) is pruned at cycle end — it LANDED. */
function hasLanded(e: QueueEntry): boolean {
  return e.status === 'failed' && e.retries < e.maxRetries
}

/** The queue's terminal states: pinned failed at maxRetries (any reason), or conflict. */
function isTerminal(e: QueueEntry): boolean {
  return isGenuinelyFailed(e) || e.status === 'conflict'
}

/** A dependent write as the registry reads it: a queue entry, or one not queued yet. */
type Dependent = Pick<QueueEntry, 'type' | 'payload'>

/**
 * The twin of `entry` in `queue` that satisfies `match`, or null. A NON-terminal match wins over a
 * terminal one regardless of queue order; the first terminal match is the answer only when no
 * non-terminal one exists. WHY: an older pinned v2 and a newer pending v2 on one key (the rep
 * re-submitted after a final refusal) — first-match would read the pinned one and pin the v8
 * `predecessor-failed`, although the pending v2 may still land and unblock it.
 */
function findPredecessor(
  entry: Dependent,
  queue: readonly QueueEntry[],
  match: (candidate: QueueEntry) => boolean,
): { predecessor: QueueEntry; spec: QueuePredecessor } | null {
  const spec = predecessors.get(entry.type)
  if (!spec) return null
  const key = spec.keyOf(entry.payload)
  if (key === null) return null
  let firstTerminal: QueueEntry | null = null
  for (const e of queue) {
    if (e === entry || e.type !== spec.predecessorType || !match(e) || spec.keyOf(e.payload) !== key) continue
    if (!isTerminal(e)) return { predecessor: e, spec }
    if (firstTerminal === null) firstTerminal = e
  }
  return firstTerminal === null ? null : { predecessor: firstTerminal, spec }
}

/**
 * The still-unlanded predecessor that blocks `entry` in `queue`, or null when `entry`
 * may replay. Reads the in-memory cycle copy, so a predecessor that synced EARLIER IN
 * THE SAME FLUSH (already flipped to the synced sentinel) unblocks its dependent at once.
 */
export function blockingPredecessor(entry: QueueEntry, queue: readonly QueueEntry[]): QueueEntry | null {
  const found = findPredecessor(entry, queue, (e) => !hasLanded(e))
  return found === null ? null : found.predecessor
}

export type PredecessorVerdict =
  | { kind: 'free' }
  | { kind: 'waiting'; predecessor: QueueEntry }
  | { kind: 'failed'; predecessor: QueueEntry; reasonCode: string }

/** What `entry`'s predecessor allows this cycle — see the file header for the three fates. */
export function predecessorVerdict(entry: Dependent, queue: readonly QueueEntry[]): PredecessorVerdict {
  const found = findPredecessor(entry, queue, (e) => !hasLanded(e))
  if (found === null) return { kind: 'free' }
  if (isTerminal(found.predecessor)) {
    return { kind: 'failed', predecessor: found.predecessor, reasonCode: found.spec.failedReasonCode }
  }
  return { kind: 'waiting', predecessor: found.predecessor }
}

/** The registration of dependent `type` and the key `payload` relates by. THROWS when there is none:
 *  nothing to relate, and any answer would be a guess. `who` names the caller in the error. */
function relationOf(who: string, type: string, payload: unknown): { spec: QueuePredecessor; key: string } {
  const spec = predecessors.get(type)
  if (!spec) throw new Error(`${who}: queue type "${type}" declares no predecessor`)
  const key = spec.keyOf(payload)
  if (key === null) throw new Error(`${who}: the "${type}" payload carries no key to find its predecessor by`)
  return { spec, key }
}

/** predecessorFate's answer: the three verdicts, plus the fate the queue alone cannot see. */
export type PredecessorFate = PredecessorVerdict['kind'] | 'discarded'

/**
 * Where the predecessor of a dependent write stands, for a caller that is ABOUT TO send or queue
 * that write and only knows its predecessor "was queued once":
 *   waiting   — still in flight in the outbox: queue the dependent behind it;
 *   failed    — terminal in the outbox (pinned, or conflict): it never landed;
 *   discarded — the rep discarded it before it landed: it never will;
 *   free      — no unlanded predecessor on this key and none discarded: it landed (a flush
 *               pruned it), so the dependent may be sent.
 * THROWS when `type` declares no predecessor or `payload` carries no key: there is nothing to
 * relate, and answering 'free' would send the dependent on a guess.
 */
export async function predecessorFate(type: string, payload: unknown): Promise<PredecessorFate> {
  const { spec, key } = relationOf('predecessorFate', type, payload)
  const verdict = predecessorVerdict({ type, payload }, await readQueue())
  if (verdict.kind !== 'free') return verdict.kind
  const discarded = await readDiscardedPredecessors()
  return discarded.some((d) => d.type === spec.predecessorType && d.key === key) ? 'discarded' : 'free'
}

/**
 * Remember that `removed` — leaving the queue by DISCARD, `remaining` being the queue without
 * it — was an unlanded predecessor, so predecessorFate can still say so once it is gone.
 * Nothing is recorded when it had landed, when it is no registered predecessor type or carries
 * no key, or while another unlanded predecessor on the same key is still in `remaining` (that
 * one decides the key's fate). Called BEFORE the queue is rewritten: a record that cannot be
 * written aborts the discard, it never leaves a discarded predecessor reading as landed.
 */
export async function recordDiscardedPredecessor(removed: QueueEntry, remaining: readonly QueueEntry[]): Promise<void> {
  if (hasLanded(removed)) return
  const keys = new Set<string>()
  for (const spec of predecessors.values()) {
    const key = spec.predecessorType === removed.type ? spec.keyOf(removed.payload) : null
    if (key === null) continue
    const covered = remaining.some((e) => e.type === removed.type && !hasLanded(e) && spec.keyOf(e.payload) === key)
    if (!covered) keys.add(key)
  }
  if (keys.size === 0) return
  // Recording and forgetting both read the whole list and write it back; two that overlap would
  // read the same list and the later write erase the earlier (a record lost — a discarded v2 then
  // reads as landed — or a forgotten one back). updateDiscardedPredecessors serialises them (P3-4).
  await updateDiscardedPredecessors((discarded) => {
    const known = (key: string): boolean => discarded.some((d) => d.type === removed.type && d.key === key)
    const added = [...keys].filter((key) => !known(key)).map((key) => ({ type: removed.type, key }))
    return added.length > 0
      ? { next: [...discarded, ...added], result: undefined }
      : { unchanged: true, result: undefined }
  })
}

/**
 * Drop the discard record that answers predecessorFate for the dependent write (`type`, `payload`)
 * — called by the OWNER of that dependent once it will never send or queue it from this device (the
 * draft that owed it was replaced by the next visit). The record exists only for that question, so
 * this is what BOUNDS the store: one record per dependent that may still ask, never one per discard
 * forever. Returns how many records were dropped (0 = none for that key; nothing is written then).
 * THROWS like predecessorFate when there is nothing to relate — it never forgets on a guess.
 */
export async function forgetDiscardedPredecessor(type: string, payload: unknown): Promise<number> {
  const { spec, key } = relationOf('forgetDiscardedPredecessor', type, payload)
  return updateDiscardedPredecessors((discarded) => {
    const kept = discarded.filter((d) => !(d.type === spec.predecessorType && d.key === key))
    const dropped = discarded.length - kept.length
    return dropped > 0 ? { next: kept, result: dropped } : { unchanged: true, result: 0 }
  })
}

/**
 * Re-pend, IN PLACE, every dependent pinned `predecessor-failed` whose predecessor is
 * back in the flush lane (pending). Returns how many moved.
 *
 * Called AFTER a caller re-pended the retryable failed entries (queueExit's manual retry,
 * queueRecovery's on-load pass): the dependent is not judged by the retryable-reason
 * classifier on its own — its fate IS its predecessor's. A predecessor that stayed pinned
 * (deterministic) or is no longer in the queue (discarded) leaves the dependent pinned,
 * so it is never replayed without the write it supplements.
 */
export function rePendDependentsBehindPendingPredecessors(queue: QueueEntry[]): number {
  let moved = 0
  for (const entry of queue) {
    if (!isGenuinelyFailed(entry) || entry.failureReason !== PREDECESSOR_FAILED_REASON) continue
    if (findPredecessor(entry, queue, (e) => e.status === 'pending') === null) continue
    entry.status = 'pending'
    entry.retries = 0
    delete entry.failureReason
    moved++
  }
  return moved
}

export interface OrphanedDependent {
  dependent: QueueEntry
  /** The registration's `discardedReasonCode` for this dependent's type. */
  reasonCode: string
}

/**
 * The PENDING dependents that lose their only unlanded predecessor when `removed` leaves
 * the queue. `remaining` is the queue WITHOUT `removed`.
 *
 * Empty when `removed` had already landed (its dependents are legitimately free), when it
 * is no registered predecessor type, or when another unlanded predecessor on the same key
 * is still in `remaining` (the dependent keeps waiting behind that one). A dependent that
 * is already pinned is not returned: it is terminal and was captured when it was pinned.
 */
export function dependentsOrphanedByRemoval(
  removed: QueueEntry,
  remaining: readonly QueueEntry[],
): OrphanedDependent[] {
  if (hasLanded(removed)) return []
  const orphaned: OrphanedDependent[] = []
  for (const candidate of remaining) {
    if (candidate.status !== 'pending') continue
    const spec = predecessors.get(candidate.type)
    if (!spec || spec.predecessorType !== removed.type) continue
    const key = spec.keyOf(candidate.payload)
    if (key === null || spec.keyOf(removed.payload) !== key) continue
    if (findPredecessor(candidate, remaining, (e) => !hasLanded(e)) !== null) continue
    orphaned.push({ dependent: candidate, reasonCode: spec.discardedReasonCode })
  }
  return orphaned
}

/** Payload key reader for the visit-report doors: the string `source_event_id`, else null. */
export function sourceEventIdOf(payload: unknown): string | null {
  if (payload === null || typeof payload !== 'object') return null
  const key = (payload as { source_event_id?: unknown }).source_event_id
  return typeof key === 'string' && key !== '' ? key : null
}
